const WASH_SELECTOR = "[data-background-wash]";
const SITE_SELECTOR = ".site-container";
const MAIN_SELECTOR = ".site-main";
const FOOTER_SELECTOR = ".site-footer";

function toPixelValue(value) {
	const numeric = Number.parseFloat(value);

	return Number.isFinite(numeric) ? numeric : 0;
}

function getBackgroundWashElements() {
	const site = document.querySelector(SITE_SELECTOR);
	const main = document.querySelector(MAIN_SELECTOR);
	const footer = document.querySelector(FOOTER_SELECTOR);
	const washes = main ? [...main.querySelectorAll(WASH_SELECTOR)] : [];

	return { footer, main, site, washes };
}

function prepareRuntimeMeasurement(site, washes) {
	site?.style.removeProperty("--site-min-height");

	for (const wash of washes) {
		// Keep large art-directed fallback heights out of scrollHeight while the
		// natural content height is measured. The fallbacks remain available
		// before JavaScript initializes and if JavaScript is unavailable.
		wash.style.setProperty("--wash-runtime-height", "0px");
		wash.style.setProperty("--wash-runtime-offset-y", "0px");
		wash.classList.add("has-runtime-sizing");
	}
}

function syncBackgroundWashes() {
	const { footer, main, site, washes } = getBackgroundWashElements();

	if (!site || !main || !footer || washes.length === 0) {
		return;
	}

	const configuredOffsets = washes.map((wash) => {
		wash.classList.remove("has-runtime-sizing");
		const offset = toPixelValue(window.getComputedStyle(wash, "::before").top);
		wash.classList.add("has-runtime-sizing");
		return offset;
	});

	prepareRuntimeMeasurement(site, washes);

	const siteRect = site.getBoundingClientRect();
	const siteDocumentTop = siteRect.top + window.scrollY;
	const anchorTops = washes.map((wash) => {
		const rect = wash.getBoundingClientRect();

		return rect.top + window.scrollY - siteDocumentTop;
	});

	// The first wash always reaches the top of the document, even though its
	// zero-height anchor lives inside the main content flow. Later washes begin
	// exactly where their blocks were inserted by the content editor.
	const washStarts = anchorTops.map((anchorTop, index) =>
		index === 0 ? -siteDocumentTop : anchorTop + configuredOffsets[index],
	);
	const naturalSiteHeight = site.scrollHeight;
	const viewportBottom = window.innerHeight - siteDocumentTop;
	const targetSiteHeight = Math.max(naturalSiteHeight, viewportBottom);

	site.style.setProperty("--site-min-height", `${targetSiteHeight}px`);

	for (const [index, wash] of washes.entries()) {
		const start = washStarts[index];
		const end = washStarts[index + 1] ?? targetSiteHeight;
		const height = Math.max(0, end - start + (index < washes.length - 1 ? 1 : 0));
		const offset = start - anchorTops[index];

		wash.style.setProperty("--wash-runtime-height", `${height}px`);
		wash.style.setProperty("--wash-runtime-offset-y", `${offset}px`);
		wash.classList.add("has-runtime-sizing");
	}
}

export function initBackgroundWash() {
	let frameId = null;
	const { footer, main, site, washes } = getBackgroundWashElements();

	const scheduleSync = () => {
		if (frameId) {
			return;
		}

		frameId = window.requestAnimationFrame(() => {
			frameId = null;
			syncBackgroundWashes();
		});
	};

	window.addEventListener("resize", scheduleSync, { passive: true });
	window.addEventListener("load", scheduleSync, { once: true });

	if (site && main && footer && window.ResizeObserver) {
		const resizeObserver = new ResizeObserver(scheduleSync);
		resizeObserver.observe(site);
		resizeObserver.observe(main);
		resizeObserver.observe(footer);

		for (const wash of washes) {
			resizeObserver.observe(wash);
		}
	}

	scheduleSync();
}
